// ** MUI Imports
import { useEffect, useState } from "react";
import { createGenericContext } from "../../utils/createGenericContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import LoginDataService from "../../services/LoginService";

export interface ILoginContext {
  error: string | null;
  setLoginError: (error: string | null) => void;
  loginState: LoginState;
  maskedEmail: string | null;
  sendMagicLink: (email: string) => void;
  passcode: string;
  setPasscode: (entered: string) => void;
  verifyOneTimeCode: () => void;
  submitting: boolean;
  maskedPhone: string | null;
  resendOneTimeCode: () => void;
  accounts: any[];
  selectAccount: (userId: string) => void;
}

export enum LoginState {
  LOADING,
  INVALID,
  ONETIMECODE,
  ALREADYENROLLED,
  OTPFIRST,
  MAGICLINKSENT,
  MAGICLINK,
  SELECTACCOUNT,
}

const [useLogin, LoginContextProvider] = createGenericContext<ILoginContext>();

const LoginProvider = (props: any) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loginState, setLoginState] = useState<LoginState>(LoginState.LOADING);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [maskedPhone, setMaskedPhone] = useState<string | null>(null);
  const [enteredPasscode, setEnteredPasscode] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [phoneId, setPhoneId] = useState<string | null>("");
  const [internalOtpToken, setOTPToken] = useState<string>("");
  const [accounts, setAccounts] = useState<any[]>([]);

  useEffect(() => {
    let s = searchParams.get("s");
    let token = searchParams.get("token");

    if (s) {
      LoginDataService.verifyEnrolLink(s)
        .then((r) => {
          handleState(
            r.data.status,
            r.data.identifier,
            r.data.maskedPhone,
            r.data.token
          );
        })
        .catch((ex) => {
          handleState("LINKINVALID", null);
        });
    } else if (token) {
      LoginDataService.verifyMagicLink(token)
        .then((r) => {
          handleState(
            r.data.status,
            r.data.identifier,
            undefined,
            r.data.token
          );
        })
        .catch((ex) => {
          handleState("LINKINVALID", null);
        });

      // verify the token, then SMS.
      handleState("MAGICLINKCLICKED", null);
    } else {
      handleState("LOGIN", null);
    }
  }, [searchParams]);

  const setLoginError = (theError: string | null) => {
    setError(theError);
  };

  const setPasscode = (p: string) => setEnteredPasscode(p);

  const sendMagicLink = (email: string) => {
    setSubmitting(true);

    LoginDataService.sendMagicLink(email)
      .then((r: any) => {
        if (r.status === 200) setLoginState(LoginState.MAGICLINKSENT);
        else
          setError(
            "Error sending magic link, please try again, or contact the service if this error persists."
          );

        setSubmitting(false);
      })
      .catch((ex: any) => {
        setError(
          "Error sending magic link, please try again, or contact the service if this error persists."
        );
        setSubmitting(false);
      });
  };

  const resendOneTimeCode = () => {
    setSubmitting(true);
    
    LoginDataService.resendOneTimeCode(phoneId!)
      .then((r) => {
        setLoginState(LoginState.ONETIMECODE);
        setPhoneId(r.data.identifier)
        setOTPToken(r.data.token!);
        setSubmitting(false);
      })
      .catch((ex) => {
        setSubmitting(false);
        setError("Error resending one time code, please try again");
      });
  };

  const selectAccount = (userId: string) => {
    setSubmitting(true);

    LoginDataService.selectAccount(userId!)
      .then((r:any) => {
        navigate("/");
        setSubmitting(false)
      })
      .catch((ex: any) => {
        setError("Error resending one time code, please try again");
        setSubmitting(false)
      });
  };


  const verifyOneTimeCode = () => {
    LoginDataService.verifyOneTimeCode(
      phoneId!,
      enteredPasscode,
      internalOtpToken
    )
      .then((r) => {
        if (r.data.success) {
          setError(null);

          if (r.data.accounts && r.data.accounts.length > 0) {
            handleState("SELECTACCOUNT", "");
            setAccounts(r.data.accounts);
          } else {
            navigate("/");
          }
        } else {
          setError(
            "You have entered an invalid code, please correct and try again."
          );
        }
      })
      .catch((ex: any) => {
        setError(
          "You have entered an invalid code, please correct and try again."
        );
      });
  };

  const handleState = (
    state: string,
    id: string | null,
    maskedPhone?: string,
    otpToken?: string
  ) => {
    switch (state) {
      case "LOGIN":
        setLoginState(LoginState.MAGICLINK);
        setLoginError(null);
        break;
      case "OTPFIRST":
        setLoginState(LoginState.OTPFIRST);
        setPhoneId(id);
        setMaskedPhone(maskedPhone!);
        setLoginError(null);
        setOTPToken(otpToken!);
        break;
      case "LINKINVALID":
        setLoginState(LoginState.INVALID);
        setLoginError(
          "You have either followed an invalid link or the link has expired."
        );
        break;
      case "ONETIMECODE":
        setLoginState(LoginState.ONETIMECODE);
        setLoginError(null);
        setMaskedEmail(null);
        setPhoneId(id);
        setOTPToken(otpToken!);
        break;
      case "ALREADYENROLLED":
        setLoginState(LoginState.INVALID);
        setLoginError("The link you have followed is no longer valid.");
        break;
      case "ACCOUNTLOCKED":
        setLoginState(LoginState.INVALID);
        setLoginError(
          "You have exceeded the maximum attempts to access the service, please contact support."
        );
        break;
      case "SELECTACCOUNT":
        setLoginState(LoginState.SELECTACCOUNT)
        break;
    }
  };

  let contextValue = {
    error,
    setLoginError,
    loginState,
    maskedEmail,
    passcode: enteredPasscode,
    setPasscode,
    verifyOneTimeCode,
    sendMagicLink,
    submitting,
    maskedPhone,
    resendOneTimeCode,
    accounts,
    selectAccount
  };

  return (
    <LoginContextProvider value={contextValue}>
      {props.children}
    </LoginContextProvider>
  );
};

export { LoginProvider, useLogin };
